import React, { useId } from 'react';

import {
  MISSING_VALUE,
  formatBiasRemoval,
  formatCropRange,
  formatModel,
  formatRunTime,
} from 'helper/flux/myStudyBlock';
import { formatDateOnly } from 'helper/flux/studyDates';
import { MyStudyBlock } from 'types/Flux/Personalized/myStudies';

import './index.scss';

export interface BlockCardProps {
  /** Bloco de um cenário (`getMyStudyScenarios`, #1993). */
  block: MyStudyBlock;
  /** Posição do bloco no cenário, a partir de 1: o título "Bloco N". */
  position: number;
  /**
   * Rótulo do membro, já resolvido pelo painel (`resolveMemberLabel` sobre o `painel-options` do
   * Flux); null quando o bloco não tem membro.
   */
  memberLabel: string | null;
}

/**
 * Cartão de um bloco no painel da linha aberta de Meus Estudos (#2002, .dc.html:334-342). Novo
 * de propósito: o `BlockDisplay` e o `Row/Scenario` são do formulário Novo Estudo e do mobile.
 *
 * - Cabeçalho: "Bloco N" e a rodada ("Rodada 00h UTC"); sem rodada, o rótulo some.
 * - Corpo, em 2 colunas: Modelo, Viés, Previsão, Membro, Início, Fim e Recorte. As datas são dias
 *   civis (`formatDateOnly`), e o Recorte é o intervalo de datas (D11). O valor que falta vira
 *   "—" e o campo fica no lugar, para a grade não trocar as colunas de bloco para bloco.
 *
 * O cartão é um grupo com o nome do bloco; os campos são uma lista de definição (`dl`).
 */
export default function BlockCard({ block, position, memberLabel }: BlockCardProps) {
  const titleId = useId();
  const runTime = formatRunTime(block.runTime);
  const fields: readonly (readonly [label: string, value: string])[] = [
    ['Modelo', formatModel(block.model)],
    ['Viés', formatBiasRemoval(block.biasRemoval)],
    ['Previsão', formatDateOnly(block.forecastDate)],
    ['Membro', memberLabel ?? MISSING_VALUE],
    ['Início', formatDateOnly(block.start)],
    ['Fim', formatDateOnly(block.end)],
    ['Recorte', formatCropRange(block.cropStart, block.cropEnd)],
  ];

  return (
    <div className="my-studies-block" role="group" aria-labelledby={titleId}>
      <div className="my-studies-block-header">
        <span id={titleId}>{`Bloco ${position}`}</span>
        {runTime && <span className="my-studies-block-run-time">{runTime}</span>}
      </div>
      <dl className="my-studies-block-fields">
        {fields.map(([label, value]) => (
          <div key={label} className="my-studies-block-field">
            <dt>{`${label}:`}</dt>
            {' '}
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
